import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  Package,
  Eye,
  ArrowRight,
  CheckCircle2,
  X,
} from 'lucide-react';
import api from '../../lib/api';

export function ProductImportTool({ onImportComplete }) {
  const [productImportFile, setProductImportFile] = useState(null);
  const [productImportPreview, setProductImportPreview] = useState(null);
  const [productImporting, setProductImporting] = useState(false);
  const [productImportResults, setProductImportResults] = useState(null);
  const [productPreviewLoading, setProductPreviewLoading] = useState(false);
  const [updateExistingProducts, setUpdateExistingProducts] = useState(false);

  const handleProductFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProductImportFile(file);
    setProductImportResults(null);
    setProductPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/products/import-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProductImportPreview(response.data);
    } catch (error) {
      toast.error('Failed to preview file: ' + (error.response?.data?.detail || error.message));
      setProductImportFile(null);
    } finally {
      setProductPreviewLoading(false);
    }
  };

  const handleImportProducts = async () => {
    if (!productImportFile) return;
    setProductImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', productImportFile);
      const response = await api.post(`/products/import-accountsiq?update_existing=${updateExistingProducts}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProductImportResults(response.data);
      toast.success(`Imported ${response.data.imported} new products`);
      onImportComplete?.();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProductImporting(false);
    }
  };

  const resetProductImport = () => {
    setProductImportFile(null);
    setProductImportPreview(null);
    setProductImportResults(null);
    setUpdateExistingProducts(false);
  };

  return (
    <Card className="border-green-200 bg-green-50/30" data-testid="product-import-tool">
      <CardHeader>
        <CardTitle className="text-green-700 flex items-center gap-2">
          <Package className="w-5 h-5" />
          Product Import Tool
        </CardTitle>
        <CardDescription>Import products from Excel files</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* File Upload */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="product-import-file" className="text-sm font-medium">Select Excel File (.xlsx)</Label>
            <Input
              id="product-import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleProductFileSelect}
              className="mt-2"
              data-testid="product-import-file-input"
            />
          </div>
          {productImportFile && (
            <Button variant="ghost" size="sm" onClick={resetProductImport}>
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Preview Loading */}
        {productPreviewLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Analyzing file...
          </div>
        )}

        {/* Preview Results */}
        {productImportPreview && !productImportResults && (
          <div className="space-y-4 p-4 bg-white rounded-lg border">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview
              </h4>
              <span className="text-xs text-muted-foreground">
                Sheet: {productImportPreview.selected_sheet}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-2xl font-bold">{productImportPreview.total_rows}</p>
                <p className="text-xs text-muted-foreground">Total Rows</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded">
                <p className="text-2xl font-bold text-green-600">{productImportPreview.new_products}</p>
                <p className="text-xs text-muted-foreground">New Products</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded">
                <p className="text-2xl font-bold text-blue-600">{productImportPreview.existing_products}</p>
                <p className="text-xs text-muted-foreground">Already Exist</p>
              </div>
            </div>

            {Object.keys(productImportPreview.mapping || {}).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Detected Columns:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(productImportPreview.mapping).map(([field, col]) => (
                    <span key={field} className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                      {field} → {col}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {productImportPreview.notes && (
              <div className="p-2 bg-blue-50 rounded text-sm text-blue-800">
                {productImportPreview.notes}
              </div>
            )}

            {productImportPreview.sample_rows?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Sample Data:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left">Name</th>
                        <th className="border p-2 text-left">Weight/Unit</th>
                        <th className="border p-2 text-left">Units/Case</th>
                        <th className="border p-2 text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productImportPreview.sample_rows.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="border p-2">{row.name || '-'}</td>
                          <td className="border p-2">{row.weight_per_unit || '-'}</td>
                          <td className="border p-2">{row.units_per_case || '-'}</td>
                          <td className="border p-2 truncate max-w-[200px]">{row.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {productImportPreview.warnings?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {productImportPreview.warnings.map((warning, idx) => (
                      <p key={idx} className="text-sm text-amber-800">{typeof warning === 'string' ? warning : warning.message}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Update Existing Option */}
            {productImportPreview.existing_products > 0 && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <Checkbox
                  id="update-existing"
                  checked={updateExistingProducts}
                  onCheckedChange={setUpdateExistingProducts}
                />
                <Label htmlFor="update-existing" className="text-sm text-blue-800">
                  Update {productImportPreview.existing_products} existing products with new data
                </Label>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={resetProductImport}>Cancel</Button>
              <Button
                onClick={handleImportProducts}
                disabled={productImporting || (productImportPreview.new_products === 0 && !updateExistingProducts)}
                data-testid="btn-confirm-product-import"
              >
                {productImporting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                ) : (
                  <><ArrowRight className="w-4 h-4 mr-2" /> Import Products</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Import Results */}
        {productImportResults && (
          <div className="p-4 bg-white rounded-lg border space-y-3">
            <h4 className="font-medium flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-4 h-4" /> Import Complete
            </h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-green-50 rounded">
                <p className="text-xl font-bold text-green-600">{productImportResults.imported}</p>
                <p className="text-xs">New</p>
              </div>
              <div className="p-2 bg-blue-50 rounded">
                <p className="text-xl font-bold text-blue-600">{productImportResults.updated || 0}</p>
                <p className="text-xs">Updated</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="text-xl font-bold text-gray-600">{productImportResults.skipped || 0}</p>
                <p className="text-xs">Skipped</p>
              </div>
            </div>
            {productImportResults.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                  {productImportResults.errors.slice(0, 10).map((err, idx) => (
                    <p key={idx} className="text-red-600">Row {err.row}: {err.error}</p>
                  ))}
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={resetProductImport} className="mt-2">Import Another File</Button>
          </div>
        )}

        {/* Help Text */}
        {!productImportFile && (
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Supported formats:</p>
            <ul className="list-disc list-inside ml-2 text-xs">
              <li>Excel file with columns: Product Name, Weight per Unit, Units per Case, Description</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
